import React from 'react';
import { Slide, FadeIn, SlideHeading, StaggerContainer, StaggerItem, Card } from './Shared';
import { Palette, Layers as LayersIcon, SlidersHorizontal, Truck, Printer, Shirt, Scissors, Sparkles, FileText, MessageSquare, PencilRuler, PackageCheck, Mail, Phone, Globe, FlaskConical, PenTool, Factory, MapPin, Rocket, Star, PackageOpen, Building2 } from 'lucide-react';
const BRAND_LOGOS = [{
  name: 'Nike',
  logo: 'https://upload.wikimedia.org/wikipedia/commons/a/a6/Logo_NIKE.svg'
}, {
  name: 'Disney',
  logo: 'https://cdn.magicpatterns.com/uploads/vQ7YYAQZ7DmpPfNdG2TByD/pasted-image.jpg'
}, {
  name: 'Walmart',
  logo: 'https://cdn.magicpatterns.com/uploads/3RAwrjVvpAhcfJrfsLch1z/pasted-image.webp'
}, {
  name: 'Target',
  logo: 'https://upload.wikimedia.org/wikipedia/commons/9/9a/Target_logo.svg'
}, {
  name: 'Warner Bros.',
  logo: 'https://cdn.magicpatterns.com/uploads/dc76PgL5dSCSkMWsfrDVAY/image.png'
}, {
  name: 'Gap',
  logo: 'https://cdn.magicpatterns.com/uploads/s6yQBfQWJ33P2fVqpawmQm/image.png'
}, {
  name: 'Old Navy',
  logo: 'https://cdn.magicpatterns.com/uploads/j7umHuLo9VNp39wK6PG6fD/image.png'
}, {
  name: 'Marvel',
  logo: 'https://cdn.magicpatterns.com/uploads/fKZZ7rr4B8L8au5WmnpBnC/image.png'
}];
export const SlideWhoWeAre = () => <Slide id="slide-who" bg="gray" data-id="element-11">
    <SlideHeading tag="Who We Are" title="A Production House Built on Experience" subtitle="30+ years of world-class textile production — a binational creative and apparel powerhouse spanning San Diego and Mexico." data-id="element-12" />

    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mt-4" data-id="element-13">
      <FadeIn delay={0.2} data-id="element-14">
        <p className="text-xl md:text-2xl text-slate-700 leading-relaxed mb-6 font-medium" data-id="element-15">
          We are a full-service custom apparel production house. From the first
          sketch to the final stitch, everything happens under our own roof.
        </p>
        <p className="text-lg text-slate-600 leading-relaxed" data-id="element-16">
          Our team has produced apparel at the highest level in the industry.
          Now we bring that same standard to brands, businesses, and events that
          refuse to look like everyone else.
        </p>
      </FadeIn>

      <FadeIn delay={0.4} data-id="element-17">
        <Card dark className="p-10" data-id="element-18">
          <p className="text-2xl md:text-3xl font-serif font-bold text-white italic leading-tight" data-id="element-19">
            "We are not here because we are beautiful. We are here to kick ass."
          </p>
        </Card>
      </FadeIn>
    </div>

    <FadeIn delay={0.5} data-id="element-20">
      <div className="border-t-2 border-black pt-10 mt-16" data-id="element-21">
        <div className="flex items-center gap-4 mb-8" data-id="element-22">
          <span className="text-sm font-bold uppercase tracking-widest text-slate-500" data-id="element-23">
            Our Production Experience Includes Work For
          </span>
          <div className="h-px bg-slate-300 flex-1" data-id="element-24" />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-8 md:gap-10 items-center" data-id="element-25">
          {BRAND_LOGOS.map((brand, i) => <div key={i} className="flex items-center justify-center h-16" data-id="element-26">
              <img src={brand.logo} alt={brand.name} className="max-h-12 max-w-full object-contain opacity-100 hover:grayscale hover:opacity-80 transition-all duration-300" data-id="element-27" />
            </div>)}
        </div>
      </div>
    </FadeIn>
  </Slide>;
export const SlideCrossBorder = () => {
  const nodes = [{
    icon: FlaskConical,
    location: 'San Diego, CA',
    role: 'R&D & Advanced Production',
    desc: 'Our innovation hub — the most advanced digital and screen printing techniques, dialed in and tested before they ever touch a production run.'
  }, {
    icon: PenTool,
    location: 'Mexico Office',
    role: 'Design & Production',
    desc: 'A dedicated team of designers and a full production office, giving us creative firepower and capacity right across the border.'
  }, {
    icon: Factory,
    location: 'Mexico Partners',
    role: 'Mass Production',
    desc: 'Trusted partner workshops built for high-volume programs — when a project needs scale, we have the network to deliver it.'
  }];
  return <Slide id="slide-border" bg="white" data-id="element-28">
      <SlideHeading tag="Our Footprint" title="The Cross-Border Advantage" subtitle="Strategically positioned on the U.S.–Mexico border between Tijuana and Tecate — combining American R&D with the creative and production power of Mexico." data-id="element-29" />

      <StaggerContainer className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-4" data-id="element-30">
        {nodes.map((node, i) => <StaggerItem key={i} data-id="element-31">
            <Card className="h-full p-8 flex flex-col" data-id="element-32">
              <div className="w-14 h-14 bg-black flex items-center justify-center mb-6" data-id="element-33">
                <node.icon className="w-7 h-7 text-white" data-id="element-34" />
              </div>
              <div className="flex items-center gap-2 mb-1" data-id="element-35">
                <MapPin className="w-4 h-4 text-slate-400" data-id="element-36" />
                <span className="text-sm font-bold uppercase tracking-widest text-slate-500" data-id="element-37">
                  {node.location}
                </span>
              </div>
              <h4 className="text-2xl font-serif font-bold text-black uppercase mb-3 leading-tight" data-id="element-38">
                {node.role}
              </h4>
              <p className="text-slate-600 text-base leading-relaxed" data-id="element-39">
                {node.desc}
              </p>
            </Card>
          </StaggerItem>)}
      </StaggerContainer>

      <FadeIn delay={0.5} data-id="element-40">
        <div className="border-l-4 border-black pl-6 mt-12 max-w-4xl" data-id="element-41">
          <p className="text-xl md:text-2xl font-serif font-bold text-black leading-tight" data-id="element-42">
            Two countries. One team. The speed and innovation of the U.S. with
            the craft and capacity of Mexico.
          </p>
        </div>
      </FadeIn>
    </Slide>;
};
export const SlideFocus = () => {
  const pillars = [{
    icon: Rocket,
    title: 'Brand Development',
    desc: 'We build and grow our own apparel brands from the ground up — design, production, and launch.'
  }, {
    icon: Star,
    title: 'Influencer Brands',
    desc: 'We manage and distribute apparel lines for influencers and creators — from concept to customer.'
  }, {
    icon: PackageOpen,
    title: 'Printing & Fulfillment',
    desc: 'End-to-end production and fulfillment — we print it, pack it, and get it where it needs to go.'
  }, {
    icon: Building2,
    title: 'B2B Production',
    desc: 'Premium custom apparel production for businesses, corporates, and events that demand quality.'
  }];
  return <Slide id="slide-focus" bg="gray" data-id="element-43">
      <SlideHeading tag="Our Focus" title="How We Partner" subtitle="More than a print shop — a creative partner that builds brands, powers creators, and produces at scale." data-id="element-44" />

      <StaggerContainer className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-4" data-id="element-45">
        {pillars.map((pillar, i) => <StaggerItem key={i} data-id="element-46">
            <Card dark className="h-full p-8 flex items-start gap-5" data-id="element-47">
              <div className="w-14 h-14 bg-white flex items-center justify-center shrink-0" data-id="element-48">
                <pillar.icon className="w-7 h-7 text-black" data-id="element-49" />
              </div>
              <div data-id="element-50">
                <h4 className="text-2xl font-serif font-bold text-white uppercase mb-2 leading-tight" data-id="element-51">
                  {pillar.title}
                </h4>
                <p className="text-slate-300 text-base leading-relaxed" data-id="element-52">
                  {pillar.desc}
                </p>
              </div>
            </Card>
          </StaggerItem>)}
      </StaggerContainer>
    </Slide>;
};
export const SlideWhatWeDo = () => {
  const techniques = [{
    icon: Printer,
    title: 'Screen Printing',
    desc: 'Up to 8 colors, high-volume runs with premium finish'
  }, {
    icon: Shirt,
    title: 'DTF & DTG',
    desc: 'Direct-to-film and direct-to-garment for vivid, durable prints'
  }, {
    icon: Sparkles,
    title: 'Embroidery',
    desc: 'Premium custom stitching for a refined, branded look'
  }, {
    icon: Scissors,
    title: 'Laser Cutting & Engraving',
    desc: 'Leather patches, wood, acrylic and specialty materials'
  }];
  const departments = [{
    icon: Palette,
    title: 'In-House Art Department',
    desc: 'Custom artwork created from scratch for your company, team, or event — not just printing what you bring us.'
  }, {
    icon: LayersIcon,
    title: 'Color Separation Department',
    desc: 'Professional separations that make every print crisp, accurate, and production-ready.'
  }, {
    icon: SlidersHorizontal,
    title: 'Pre-Press Production',
    desc: 'Films, screens, and setup dialed in before a single garment runs — zero guesswork.'
  }, {
    icon: Truck,
    title: 'Logistics',
    desc: 'Coordinated production and delivery so your order arrives on time, every time.'
  }];
  return <Slide id="slide-what" bg="white" data-id="element-53">
      <SlideHeading tag="What We Do" title="Everything In-House" subtitle="From custom art to the finished garment — every step handled by our own team, across a wide range of textile products." data-id="element-54" />

      <div className="mb-12" data-id="element-55">
        <h3 className="text-sm font-bold uppercase tracking-widest text-slate-500 mb-6" data-id="element-56">
          Production Techniques
        </h3>
        <StaggerContainer className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-57">
          {techniques.map((item, i) => <StaggerItem key={i} data-id="element-58">
              <Card className="h-full p-6 flex flex-col" data-id="element-59">
                <div className="w-12 h-12 bg-black flex items-center justify-center mb-5" data-id="element-60">
                  <item.icon className="w-6 h-6 text-white" data-id="element-61" />
                </div>
                <h4 className="text-xl font-serif font-bold text-black uppercase mb-2 leading-tight" data-id="element-62">
                  {item.title}
                </h4>
                <p className="text-slate-600 text-sm font-medium" data-id="element-63">
                  {item.desc}
                </p>
              </Card>
            </StaggerItem>)}
        </StaggerContainer>
      </div>

      <div data-id="element-64">
        <h3 className="text-sm font-bold uppercase tracking-widest text-slate-500 mb-6" data-id="element-65">
          In-House Departments
        </h3>
        <StaggerContainer className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-66">
          {departments.map((item, i) => <StaggerItem key={i} data-id="element-67">
              <Card dark className="h-full p-8 flex items-start gap-5" data-id="element-68">
                <div className="w-14 h-14 bg-white flex items-center justify-center shrink-0" data-id="element-69">
                  <item.icon className="w-7 h-7 text-black" data-id="element-70" />
                </div>
                <div data-id="element-71">
                  <h4 className="text-2xl font-serif font-bold text-white uppercase mb-2 leading-tight" data-id="element-72">
                    {item.title}
                  </h4>
                  <p className="text-slate-300 text-base leading-relaxed" data-id="element-73">
                    {item.desc}
                  </p>
                </div>
              </Card>
            </StaggerItem>)}
        </StaggerContainer>
      </div>
    </Slide>;
};
export const SlideHowWeWork = () => {
  const steps = [{
    icon: MessageSquare,
    num: '01',
    title: 'Brief',
    desc: 'Tell us about your brand, project, or event. We listen and define the vision.'
  }, {
    icon: FileText,
    num: '02',
    title: 'Quote',
    desc: 'We put together a clear, honest quote — no surprises, no fluff.'
  }, {
    icon: SlidersHorizontal,
    num: '03',
    title: 'Engineering',
    desc: 'Every file is reviewed and optimized for production — refining artwork and dialing in separations so it prints flawlessly.'
  }, {
    icon: PencilRuler,
    num: '04',
    title: 'Sample & Proof',
    desc: 'Our art team mocks it up and we produce a sample for your approval.'
  }, {
    icon: Printer,
    num: '05',
    title: 'Production',
    desc: 'Once approved, we run your job in-house with full quality control.'
  }, {
    icon: PackageCheck,
    num: '06',
    title: 'Delivery',
    desc: 'Finished, inspected, and delivered on schedule — ready to wear.'
  }];
  return <Slide id="slide-how" bg="gray" data-id="element-74">
      <SlideHeading tag="How We Work" title="Simple. Direct. Done Right." subtitle="A clear process from first conversation to finished product." data-id="element-75" />

      <StaggerContainer className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-4" data-id="element-76">
        {steps.map((step, i) => <StaggerItem key={i} data-id="element-77">
            <Card className="h-full p-6 flex flex-col" data-id="element-78">
              <div className="flex items-center justify-between mb-5" data-id="element-79">
                <div className="w-12 h-12 bg-black flex items-center justify-center" data-id="element-80">
                  <step.icon className="w-6 h-6 text-white" data-id="element-81" />
                </div>
                <span className="text-4xl font-serif font-bold text-slate-200" data-id="element-82">
                  {step.num}
                </span>
              </div>
              <h4 className="text-xl font-serif font-bold text-black uppercase mb-2" data-id="element-83">
                {step.title}
              </h4>
              <p className="text-slate-600 text-sm font-medium leading-relaxed" data-id="element-84">
                {step.desc}
              </p>
            </Card>
          </StaggerItem>)}
      </StaggerContainer>
    </Slide>;
};
export const SlideCTA = () => <Slide id="slide-cta" bg="black" className="items-center text-center" data-id="element-85">
    <FadeIn data-id="element-86">
      <div className="inline-block border-2 border-white text-white text-xs md:text-sm font-bold tracking-[0.3em] px-5 py-2 mb-10 uppercase" data-id="element-87">
        Let's Work Together
      </div>
      <h2 className="text-5xl md:text-7xl lg:text-8xl font-serif font-bold text-white uppercase mb-8 leading-none" data-id="element-88">
        Let's Make Your
        <br data-id="element-89" />
        Brand Look Chingón
      </h2>
    </FadeIn>

    <FadeIn delay={0.2} className="max-w-3xl mx-auto mb-16" data-id="element-90">
      <p className="text-xl md:text-2xl text-slate-300 font-medium leading-relaxed" data-id="element-91">
        Let our team of designers, creatives, and production experts make your
        business look like never before.
      </p>
    </FadeIn>

    <FadeIn delay={0.4} data-id="element-92">
      <div className="flex items-center justify-center mb-16" data-id="element-93">
        <a href="https://stratacustomapparel.com" target="_blank" rel="noreferrer" className="bg-white hover:bg-slate-200 text-black font-bold uppercase tracking-widest text-base px-10 py-5 transition-colors duration-300 flex items-center gap-3 justify-center" data-id="element-94">
          <Globe className="w-5 h-5" data-id="element-95" />
          stratacustomapparel.com
        </a>
      </div>
    </FadeIn>

    <FadeIn delay={0.6} data-id="element-96">
      <p className="text-xl md:text-2xl font-serif font-bold text-white italic max-w-2xl mx-auto leading-tight" data-id="element-97">
        "We are not here because we are beautiful. We are here to kick ass."
      </p>
    </FadeIn>

    <FadeIn delay={0.8} className="mt-24 text-slate-500 font-bold uppercase tracking-widest text-sm" data-id="element-98">
      <p data-id="element-99">STRATA Creative Group • San Diego, CA</p>
    </FadeIn>
  </Slide>;